import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Alert, AlertDescription } from '../ui/alert';
import { useChecklist } from '../../context/ChecklistContext';
import { useApp } from '../../context/AppContext';
import QuestionEditorCard from './QuestionEditorCard';
import { Plus, Save, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const ChecklistBuilderPanel = ({ templateId, onTemplateChange }) => {
  const { templates, updateTemplate, publishTemplate, addQuestion, updateQuestion, removeQuestion, reorderQuestion } = useChecklist();
  const { isReadOnly, canEdit } = useApp();
  const [template, setTemplate] = useState(null);
  const [isEditingEffective, setIsEditingEffective] = useState(false);

  useEffect(() => {
    if (templateId) {
      const found = templates.find(t => t.id === templateId);
      if (found) {
        // Create a new object reference to trigger re-render
        setTemplate({ ...found });
        setIsEditingEffective(found.status === 'Effective');
      } else {
        setTemplate(null);
        setIsEditingEffective(false);
      }
    } else {
      setTemplate(null);
      setIsEditingEffective(false);
    }
  }, [templateId, templates]);

  const handleAddQuestion = () => {
    if (!template) return;
    const newQuestion = {
      text: '',
      responseType: 'Yes/No',
      failureCreatesDeviation: false,
      evidenceRequired: 'None',
      aiEvidenceAssist: false,
      clauseStandard: '',
      clauseReference: ''
    };
    addQuestion(template.id, newQuestion);
  };

  const handleUpdateQuestion = (questionId, updatedQuestion) => {
    if (!template) return;
    updateQuestion(template.id, questionId, updatedQuestion);
  };

  const handleDeleteQuestion = (questionId) => {
    if (!template) return;
    removeQuestion(template.id, questionId);
  };

  const handleMoveQuestion = (fromIndex, toIndex) => {
    if (!template) return;
    reorderQuestion(template.id, fromIndex, toIndex);
  };

  const handleSaveDraft = () => {
    if (!template) return;
    const errors = validateTemplate();
    if (errors.length > 0) {
      toast.error('Validation failed', { description: errors.join(', ') });
      return;
    }
    // Update template with current state
    const updatedTemplate = templates.find(t => t.id === template.id);
    if (updatedTemplate) {
      updateTemplate(template.id, {
        name: updatedTemplate.name,
        category: updatedTemplate.category,
        questions: updatedTemplate.questionsList?.length || 0,
        questionsList: updatedTemplate.questionsList
      });
      toast.success('Draft saved');
    }
  };

  const handlePublish = () => {
    if (!template) return;
    const errors = validateTemplate();
    if (errors.length > 0) {
      toast.error('Cannot publish', { description: errors.join(', ') });
      return;
    }
    publishTemplate(template.id);
    setIsEditingEffective(false);
    if (onTemplateChange) onTemplateChange();
  };

  const validateTemplate = () => {
    const errors = [];
    if (!template.name || !template.name.trim()) {
      errors.push('Template name is required');
    }
    if (!template.category) {
      errors.push('Category is required');
    }
    if (!template.questionsList || template.questionsList.length === 0) {
      errors.push('At least one question is required');
    }
    // Validate questions
    if (template.questionsList) {
      template.questionsList.forEach((q, idx) => {
        if (!q.text || !q.text.trim()) {
          errors.push(`Question ${idx + 1} text is required`);
        }
      });
    }
    return errors;
  };

  if (!template) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-slate-400" />
          <h3 className="text-lg font-medium text-slate-800 mb-2">No Template Selected</h3>
          <p className="text-sm text-slate-600">
            Select a template from the library or create a new one to start building.
          </p>
        </CardContent>
      </Card>
    );
  }

  const questions = template.questionsList || [];
  const isEffective = template.status === 'Effective';
  const canModify = canEdit && (!isEffective || isEditingEffective);

  return (
    <div className="space-y-4">
      {isEffective && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            This template is Effective. Editing will create a new draft version.
          </AlertDescription>
        </Alert>
      )}

      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <CardTitle className="text-slate-900">{template.name}</CardTitle>
                <Badge className={`${
                  template.status === 'Effective' 
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : template.status === 'Draft'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-slate-100 text-slate-600 border-slate-200'
                } text-xs`}>
                  {template.status}
                </Badge>
                <span className="text-xs text-slate-500 font-mono">v{template.version}</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span>ID: <span className="font-mono text-[#fd4816]">{template.id}</span></span>
                <span>Category: {template.category}</span>
                <span>Questions: {questions.length}</span>
              </div>
            </div>
            {canModify && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleSaveDraft}
                  className="border-slate-200"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Draft
                </Button>
                <Button
                  onClick={handlePublish}
                  className="bg-[#fd4816] hover:bg-[#ea380c] text-white"
                  disabled={template.status === 'Effective' && !isEditingEffective}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Publish
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <ScrollArea className="h-[calc(100vh-400px)]">
            <div className="space-y-4">
              {questions.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <p className="mb-4">No questions yet</p>
                  {canModify && (
                    <Button onClick={handleAddQuestion} className="bg-[#fd4816] hover:bg-[#ea380c] text-white">
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Question
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {canModify && (
                    <div className="flex justify-end mb-4">
                      <Button onClick={handleAddQuestion} variant="outline" className="border-slate-200">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Question
                      </Button>
                    </div>
                  )}
                  {questions.map((question, index) => (
                    <QuestionEditorCard
                      key={question.id}
                      question={question}
                      index={index}
                      total={questions.length}
                      onUpdate={(updated) => handleUpdateQuestion(question.id, updated)}
                      onDelete={() => handleDeleteQuestion(question.id)}
                      onMoveUp={() => handleMoveQuestion(index, index - 1)}
                      onMoveDown={() => handleMoveQuestion(index, index + 1)}
                      canEdit={canModify}
                    />
                  ))}
                </>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChecklistBuilderPanel;

